import {
  Box,
  ButtonProps,
  CircularProgress,
  Chip,
  Alert,
  Typography,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTextField from "../../components/common/CustomTextField";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import History from "../../components/History";
import ToastNotification from "../../components/ToastNotification";
import { useProfilesQuery } from "../../services/ProfilesService";
import {
  addServiceProviderApi,
  deleteServiceProviderApi,
  editServiceProviderApi,
  fetchServiceProviderHistory,
  getServiceProviderById,
  IRequestField,
  IResponseField,
  IServiceProvider,
  IServiceProviderHistory,
  useServiceProvidersQuery,
} from "../../services/ServiceProviderService";
import {
  executeServiceProviderTest,
  getTestingServiceProviderById,
  ITestingServiceProvider,
  useTestingServiceProvidersQuery,
} from "../../services/TestingServiceProviderService";
import { fetchCountry } from "../../services/CountryService";
import { fetchOperator } from "../../services/OperatorsService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ServiceProviderPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [profileOptions, setProfileOptions] = useState<Option[]>([]);
  const [profileMap, setProfileMap] = useState<
    Record<string, { name: string; accountManagerEmail: string; currencyId: string }>
  >({});
  const [filters, setFilters] = useState<Params<IServiceProvider> & { _forceRefresh?: number }>({});
  const [appliedFilters, setAppliedFilters] = useState<Params<IServiceProvider> & { _forceRefresh?: number }>({});

  const dialogRef = useRef<ServiceProviderDialogRef>(null);
  const testDialogRef = useRef<TestServiceProviderDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleOpenTestDialog = (id: string) => {
    testDialogRef.current?.openDialog(id);
  };

  const { data: profileOption } = useProfilesQuery();
  const { data: serviceProvidersData } = useServiceProvidersQuery({});

  // Build name options from fetched service providers
  const nameOptions =
    serviceProvidersData?.content?.map((provider) => ({
      label: provider.name,
      value: provider.id,
    })) || [];

  useEffect(() => {
    if (profileOption) {
      setProfileOptions(profileOption.profileOptions);
      setProfileMap(profileOption.profileMap);
    }
  }, [profileOption]);

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_SERVICE_PROVIDER"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_SERVICE_PROVIDER"),
    },
    onViewHistory: {
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_SERVICE_PROVIDERS_HISTORY"),
    },
    onTest: {
      handler: async (id: string | number) => {
        handleOpenTestDialog(String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_TESTING_SERVICE_PROVIDERS"),
    },
  });

  const typeOptions = [
    { label: "MNP", value: "MNP" },
    { label: "HLR", value: "HLR" },
    { label: "MNP/HLR", value: "MNP_HLR" },
  ];

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Name",
      value: filters.id ?? "",
      options: nameOptions,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = nameOptions.find((option) => option.value === value);
        setFilters((prev) => ({
          ...prev,
          id: selectedProvider?.value,
        }));
      },
      clearable: true,
      placeholder: "Select Name",
    },
    {
      label: "Profile",
      value: filters.profileId ?? "",
      options: profileOptions,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          profileId: String(value),
        }));
      },
      clearable: true,
      placeholder: "Select Profile",
    },
    {
      label: "Type",
      value: filters.type ?? "",
      options: typeOptions,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          type: String(value) as IServiceProvider["type"],
        }));
      },
      clearable: true,
      placeholder: "Select Type",
    },
  ];

  const handleSearchClick = () => {
    setAppliedFilters({
      ...filters,
      id: filters.id,
      _forceRefresh: Date.now(),
    });
  };

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_SERVICE_PROVIDERS",
    },
  ];

  const fields: IField<IServiceProvider>[] = [
    { key: "name", header: "Name" },
    {
      key: "profileId",
      header: "Profile",
      transform: (row) => {
        if (!row.profileId) return "Unknown";
        const profile = profileMap[row.profileId];
        return profile?.name || "Unknown";
      },
    },
    { key: "type", header: "Type" },
    { key: "protocol", header: "Protocol" },
    { key: "baseUrl", header: "Base URL" },
    {
      key: "enabled",
      header: "Status",
      transform: (row) => (row.enabled ? "Enabled" : "Disabled"),
    },
    { key: "createdBy", header: "Created By" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Service Provider"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Service Provider List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={(value) =>
            setAppliedFilters((prev) => ({ ...prev, search: value }))
          }
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_SERVICE_PROVIDER",
            },
          ]}
        />
        <CustomTable<IServiceProvider>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useServiceProvidersQuery}
          actions={actions}
          sortableFields={["name", "type", "protocol"]}
        />
      </Box>

      <ServiceProviderDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        profileOptions={profileOptions}
        profileMap={profileMap}
        appliedFilters={appliedFilters}
      />

      <TestServiceProviderDialog ref={testDialogRef} />
    </Box>
  );
};

export interface ServiceProviderDialogRef {
  openDialog: (mode: "add" | "edit" | "delete" | "history", id?: string) => void;
}

export interface TestServiceProviderDialogRef {
  openDialog: (id: string) => void;
}

interface ServiceProviderDialogProps {
  onRefresh: () => void;
  profileOptions: Option[];
  profileMap: Record<
    string,
    { name: string; accountManagerEmail: string; currencyId: string }
  >;
  appliedFilters: Params<IServiceProvider> & { _forceRefresh?: number };
}

interface DialogData extends Partial<IServiceProvider> {
  // Request field items
  apiKeyFieldName?: string;
  apiKeyFieldLocation?: string;
  msisdnFieldName?: string;
  msisdnFieldLocation?: string;
  usernameFieldName?: string;
  usernameFieldLocation?: string;
  passwordFieldName?: string;
  passwordFieldLocation?: string;
  httpMethod?: string;
  contentType?: string;
  // Response field items
  responseMcc?: string;
  responseMnc?: string;
  responseOriginalMnc?: string;
  responsePorted?: string;
  responseNotActive?: string;
  responseNotActiveValues?: string;
  responseCode?: string;
}

const ServiceProviderDialog = forwardRef<
  ServiceProviderDialogRef,
  ServiceProviderDialogProps
>(({ onRefresh, profileOptions, profileMap, appliedFilters }, ref) => {
  const initialDialogData: DialogData = {
    name: "",
    type: undefined,
    protocol: "HTTP_REST",
    baseUrl: "",
    apiKey: "",
    enabled: true,
    profileId: "",
    // Request field defaults
    apiKeyFieldName: "",
    apiKeyFieldLocation: "URL",
    msisdnFieldName: "",
    msisdnFieldLocation: "URL",
    usernameFieldName: "",
    usernameFieldLocation: "URL",
    passwordFieldName: "",
    passwordFieldLocation: "URL",
    httpMethod: "GET",
    contentType: "application/json",
    // Response field defaults
    responseMcc: "",
    responseMnc: "",
    responseOriginalMnc: "",
    responsePorted: "",
    responseNotActive: "",
    responseNotActiveValues: "",
    responseCode: "",
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<
    "add" | "edit" | "delete" | "history"
  >("add");
  const [dialogData, setDialogData] = useState<DialogData>(initialDialogData);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);

  const stepLabels =
    dialogMode === "delete"
      ? ["Delete Service Provider"]
      : ["Provider Details", "Request Field Details", "Response Field Details"];

  useImperativeHandle(ref, () => ({
    openDialog: async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string
    ) => {
      setDialogMode(mode);
      setSelectedId(id);
      setCurrentStep(0);
      setErrors({});

      try {
        if ((mode === "edit" || mode === "delete" || mode === "history") && id) {
          const entityData = await getServiceProviderById(id);
          setDialogData({
            ...initialDialogData,
            ...entityData,
            // Flatten request field
            apiKeyFieldName: entityData.requestField?.apiKey?.name || "",
            apiKeyFieldLocation: entityData.requestField?.apiKey?.location || "URL",
            msisdnFieldName: entityData.requestField?.msisdn?.name || "",
            msisdnFieldLocation: entityData.requestField?.msisdn?.location || "URL",
            usernameFieldName: entityData.requestField?.username?.name || "",
            usernameFieldLocation: entityData.requestField?.username?.location || "URL",
            passwordFieldName: entityData.requestField?.password?.name || "",
            passwordFieldLocation: entityData.requestField?.password?.location || "URL",
            httpMethod: entityData.requestField?.httpMethod || "GET",
            contentType: entityData.requestField?.contentType || "application/json",
            // Flatten response field
            responseMcc: entityData.responseField?.mcc || "",
            responseMnc: entityData.responseField?.mnc || "",
            responseOriginalMnc: entityData.responseField?.originalMnc || "",
            responsePorted: entityData.responseField?.ported || "",
            responseNotActive: entityData.responseField?.notActive || "",
            responseNotActiveValues: entityData.responseField?.notActiveValues || "",
            responseCode: entityData.responseField?.responseCode || "",
          });
        } else {
          setDialogData(initialDialogData);
        }
        setDialogOpen(true);
      } catch (error: any) {
        console.error("Error opening dialog:", error);
        ToastNotification.error("Failed to open the dialog.");
      }
    },
  }));

  // Validation effect
  useEffect(() => {
    let hasErrors = false;

    if (currentStep === 0) {
      // Step 0: Provider Details
      const step0RequiredFields = ["name", "type", "profileId", "protocol", "baseUrl"];
      hasErrors = step0RequiredFields.some((field) => {
        const value = dialogData[field as keyof DialogData];
        return value === null || value === undefined || value === "";
      });

      // Authentication validation: Either apiKey OR (username + password)
      if (!hasErrors) {
        const hasApiKey = dialogData.apiKey?.trim();
        const hasUsername = dialogData.username?.trim();
        const hasPassword = dialogData.password?.trim();
        
        if (!hasApiKey && !(hasUsername && hasPassword)) {
          hasErrors = true;
        }
      }
    } else if (currentStep === 1) {
      // Step 1: Request Field Details
      // Always required: msisdnFieldName, httpMethod
      if (!dialogData.msisdnFieldName?.trim() || !dialogData.httpMethod?.trim()) {
        hasErrors = true;
      }

      // Authentication: Either apiKeyFieldName OR (usernameFieldName + passwordFieldName)
      if (!hasErrors) {
        const hasApiKeyField = dialogData.apiKeyFieldName?.trim();
        const hasUsernameField = dialogData.usernameFieldName?.trim();
        const hasPasswordField = dialogData.passwordFieldName?.trim();
        
        if (!hasApiKeyField && !(hasUsernameField && hasPasswordField)) {
          hasErrors = true;
        }
      }
    } else if (currentStep === 2) {
      // Step 2: Response Field Details
      // Always required: responseCode
      if (!dialogData.responseCode?.trim()) {
        hasErrors = true;
      }

      // Type-specific required fields
      if (!hasErrors && dialogData.type) {
        if (dialogData.type === "HLR") {
          // MNP requires: active
          if (!dialogData.responseNotActive?.trim() || !dialogData.responseNotActiveValues?.trim()) {
            hasErrors = true;
          }
          // Check for format validation errors
          if (errors.responseNotActiveValues) {
            hasErrors = true;
          }
        } else if (dialogData.type === "MNP") {
          // HLR requires: mcc, mnc, originalMnc, ported
          if (
            !dialogData.responseMcc?.trim() ||
            !dialogData.responseMnc?.trim() ||
            !dialogData.responseOriginalMnc?.trim() ||
            !dialogData.responsePorted?.trim()
          ) {
            hasErrors = true;
          }
        } else if (dialogData.type === "MNP_HLR") {
          // MNP_HLR requires: active, mcc, mnc, originalMnc, ported
          if (
            !dialogData.responseNotActive?.trim() ||
            !dialogData.responseNotActiveValues?.trim() ||
            !dialogData.responseMcc?.trim() ||
            !dialogData.responseMnc?.trim() ||
            !dialogData.responseOriginalMnc?.trim() ||
            !dialogData.responsePorted?.trim()
          ) {
            hasErrors = true;
          }
          // Check for format validation errors
          if (errors.responseNotActiveValues) {
            hasErrors = true;
          }
        }
      }
    }

    setIsSaveDisabled(hasErrors);
    setIsNextDisabled(hasErrors && currentStep < stepLabels.length - 1);
  }, [dialogData, currentStep, stepLabels.length, errors]);

  // Auto-append suffix to name based on type when adding
  useEffect(() => {
    if (dialogMode === "add") {
      setDialogData((prev) => {
        let rawName = prev.name || "";
        if (rawName.endsWith("-MNP") || rawName.endsWith("-HLR")) {
          rawName = rawName.replace(/-MNP$|-HLR$/, "");
        }
        let newName = rawName;
        if (prev.type === "MNP") {
          newName = rawName + "-MNP";
        } else if (prev.type === "HLR") {
          newName = rawName + "-HLR";
        } else if (prev.type === "MNP_HLR") {
          newName = rawName + "-MNP/HLR";
        }
        return { ...prev, name: newName };
      });
    }
  }, [dialogData.type, dialogMode]);

  const buildPayload = (): Partial<IServiceProvider> => {
    const requestField: IRequestField = {
      apiKey: {
        name: dialogData.apiKeyFieldName || "",
        location: (dialogData.apiKeyFieldLocation as "URL" | "BODY" | "HEADER") || "URL",
      },
      msisdn: {
        name: dialogData.msisdnFieldName || "",
        location: (dialogData.msisdnFieldLocation as "URL" | "BODY" | "HEADER") || "URL",
      },
      username: {
        name: dialogData.usernameFieldName || "",
        location: (dialogData.usernameFieldLocation as "URL" | "BODY" | "HEADER") || "URL",
      },
      password: {
        name: dialogData.passwordFieldName || "",
        location: (dialogData.passwordFieldLocation as "URL" | "BODY" | "HEADER") || "URL",
      },
      httpMethod: dialogData.httpMethod || "GET",
      contentType: dialogData.contentType || "application/json",
    };

    const responseField: IResponseField = {
      mcc: dialogData.responseMcc || "",
      mnc: dialogData.responseMnc || "",
      originalMnc: dialogData.responseOriginalMnc || "",
      ported: dialogData.responsePorted || "",
      notActive: dialogData.responseNotActive || "",
      notActiveValues: dialogData.responseNotActiveValues || "",
      responseCode: dialogData.responseCode || "",
    };

    return {
      id: dialogData.id,
      name: dialogData.name,
      type: dialogData.type,
      protocol: dialogData.protocol,
      baseUrl: dialogData.baseUrl,
      apiKey: dialogData.apiKey,
      username: dialogData.username,
      password: dialogData.password,
      enabled: dialogData.enabled,
      profileId: dialogData.profileId,
      requestField,
      responseField,
      isDeleted: false,
      version: dialogData.version,
      createdBy: dialogData.createdBy,
      updatedBy: dialogData.updatedBy,
      createdDate: dialogData.createdDate,
      timestamp: dialogData.timestamp,
    };
  };

  const handleSave = async () => {
    try {
      const payload = buildPayload();

      if (dialogMode === "edit" && selectedId) {
        await editServiceProviderApi(selectedId, payload);
        ToastNotification.success("Service Provider updated successfully!");
      } else if (dialogMode === "add") {
        await addServiceProviderApi(payload);
        ToastNotification.success("Service Provider added successfully!");
      }

      setDialogOpen(false);
      onRefresh();
      setDialogData(initialDialogData);
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedId) {
        await deleteServiceProviderApi(selectedId);
        ToastNotification.success("Service Provider deleted successfully!");
        setDialogOpen(false);
        onRefresh();
      }
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const typeOptions = [
    { label: "MNP", value: "MNP" },
    { label: "HLR", value: "HLR" },
    { label: "MNP/HLR", value: "MNP_HLR" },
  ];

  const protocolOptions = [
    { label: "HTTP REST", value: "HTTP_REST" },
  ];

  const locationOptions = [
    { label: "URL", value: "URL" },
    { label: "BODY", value: "BODY" },
    { label: "HEADER", value: "HEADER" },
  ];

  const httpMethodOptions = [
    { label: "GET", value: "GET" },
    { label: "POST", value: "POST" },
  ];

  const getStepFields = () => {
    if (dialogMode === "history") {
      return [
        {
          type: "custom" as const,
          component: (
            <History
              filters={appliedFilters as any}
              fields={fieldHistory as any}
              fetchFunc={({ size = 10 }) =>
                fetchServiceProviderHistory(Number(selectedId) || 0, { size }).then((result) => ({
                  ...result,
                  content: result.content.map((item: any) => ({
                    ...item,
                    historyTimestamp: item.historyTimestamp ?? new Date(),
                    // Flatten requestField for proper diff highlighting
                    requestFieldApiKeyName: item.requestField?.apiKey?.name || "",
                    requestFieldApiKeyLocation: item.requestField?.apiKey?.location || "",
                    requestFieldMsisdnName: item.requestField?.msisdn?.name || "",
                    requestFieldMsisdnLocation: item.requestField?.msisdn?.location || "",
                    requestFieldUsernameName: item.requestField?.username?.name || "",
                    requestFieldUsernameLocation: item.requestField?.username?.location || "",
                    requestFieldPasswordName: item.requestField?.password?.name || "",
                    requestFieldPasswordLocation: item.requestField?.password?.location || "",
                    requestFieldHttpMethod: item.requestField?.httpMethod || "",
                    requestFieldContentType: item.requestField?.contentType || "",
                    // Flatten responseField for proper diff highlighting
                    responseFieldMcc: item.responseField?.mcc || "",
                    responseFieldMnc: item.responseField?.mnc || "",
                    responseFieldOriginalMnc: item.responseField?.originalMnc || "",
                    responseFieldPorted: item.responseField?.ported || "",
                    responseFieldNotActive: item.responseField?.notActive || "",
                    responseFieldNotActiveValues: item.responseField?.notActiveValues || "",
                    responseFieldResponseCode: item.responseField?.responseCode || "",
                  })),
                }))
              }
            />
          ),
        },
      ];
    }

    if (dialogMode === "delete") {
      return [
        {
          type: "custom" as const,
          component: (
            <Box>Are you sure you want to delete this Service Provider?</Box>
          ),
        },
      ];
    }

    const steps = [
      // Step 1: Provider Details
      [
        // Conditional alert: only show if authentication fields are not properly filled
        ...(() => {
          const hasApiKey = dialogData.apiKey?.trim();
          const hasUsername = dialogData.username?.trim();
          const hasPassword = dialogData.password?.trim();
          
          if (!hasApiKey && !(hasUsername && hasPassword)) {
            return [{
              type: "custom" as const,
              fullWidth: true,
              component: (
                <Alert severity="warning" sx={{ mb: 2, width: "100%" }}>
                  Provide either API Key OR both Username and Password
                </Alert>
              ),
            }];
          }
          return [];
        })(),
        {
          type: "text" as const,
          label: "Name",
          placeholder: "Provider Name",
          value: dialogData.name || "",
          error: errors.name || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.name ? "Name is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, name: String(value) }));
            setErrors((prev) => ({ ...prev, name: !value }));
          },
        },
        {
          type: "select" as const,
          label: "Type",
          placeholder: "Select Type",
          options: typeOptions,
          value: dialogData.type || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          disabled: dialogMode === "edit",
          error: errors.type || false,
          helperText: errors.type ? "Type is required." : "",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              type: String(value) as IServiceProvider["type"],
            }));
            setErrors((prev) => ({ ...prev, type: !value }));
          },
        },
        {
          type: "custom" as const,
          component: (
            <AutoComplete
              label="Profile"
              value={dialogData.profileId ?? ""}
              options={profileOptions}
              onChange={(value: string | number | (string | number)[]) => {
                if (typeof value === "string" || typeof value === "number") {
                  setDialogData((prev) => ({ ...prev, profileId: String(value) }));
                  setErrors((prev) => ({ ...prev, profileId: !value }));
                }
              }}
              clearable={true}
              placeholder="Select Profile"
              highlightLabelStar={true}
              error={errors.profileId}
              helperText={errors.profileId ? "Profile is required." : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.WhiteBackground,
                },
              }}
            />
          ),
        },
        {
          type: "select" as const,
          label: "Protocol",
          placeholder: "Select Protocol",
          options: protocolOptions,
          value: dialogData.protocol || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          error: errors.protocol || false,
          helperText: errors.protocol ? "Protocol is required." : "",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              protocol: String(value) as IServiceProvider["protocol"],
            }));
            setErrors((prev) => ({ ...prev, protocol: !value }));
          },
        },
        {
          type: "text" as const,
          label: "Base URL",
          placeholder: "Base URL",
          value: dialogData.baseUrl || "",
          error: errors.baseUrl || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.baseUrl ? "Base URL is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, baseUrl: String(value) }));
            setErrors((prev) => ({ ...prev, baseUrl: !value }));
          },
        },
        {
          type: "text" as const,
          label: "API Key",
          placeholder: "API Key",
          value: dialogData.apiKey || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, apiKey: String(value) }));
          },
        },
        {
          type: "text" as const,
          label: "Username",
          placeholder: "Username",
          value: dialogData.username || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, username: String(value) }));
          },
        },
        {
          type: "text" as const,
          label: "Password",
          placeholder: "Password",
          value: dialogData.password || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, password: String(value) }));
          },
        },
        {
          type: "select" as const,
          label: "Status",
          placeholder: "Select Status",
          options: [
            { label: "Enabled", value: "true" },
            { label: "Disabled", value: "false" },
          ],
          value: dialogData.enabled !== undefined ? String(dialogData.enabled) : "true",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              enabled: value === "true",
            }));
          },
        },
        {
          type: "text" as const,
          hidden: true,
          value: "",
          onChange: () => {},
        },
      ],
      // Step 2: Request Field Details
      [
        // Conditional alert: only show if authentication field names are not properly filled
        ...(() => {
          const hasApiKeyField = dialogData.apiKeyFieldName?.trim();
          const hasUsernameField = dialogData.usernameFieldName?.trim();
          const hasPasswordField = dialogData.passwordFieldName?.trim();
          
          if (!hasApiKeyField && !(hasUsernameField && hasPasswordField)) {
            return [{
              type: "custom" as const,
              fullWidth: true,
              component: (
                <Alert severity="warning" sx={{ mb: 2, width: "100%" }}>
                  Provide either API Key Field OR both Username and Password Fields
                </Alert>
              ),
            }];
          }
          return [];
        })(),
        {
          type: "text" as const,
          label: "API Key Field Name",
          placeholder: "e.g., api_key",
          value: dialogData.apiKeyFieldName || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "The name of the API key field in the service provider's API",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, apiKeyFieldName: String(value) }));
          },
        },
        {
          type: "select" as const,
          label: "API Key Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.apiKeyFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "Where the API key should be placed in the request",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              apiKeyFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Username Field Name",
          placeholder: "e.g., username",
          value: dialogData.usernameFieldName || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "The name of the username field in the service provider's API",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, usernameFieldName: String(value) }));
          },
        },
        {
          type: "select" as const,
          label: "Username Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.usernameFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "Where the username should be placed in the request",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              usernameFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Password Field Name",
          placeholder: "e.g., password",
          value: dialogData.passwordFieldName || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "The name of the password field in the service provider's API",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, passwordFieldName: String(value) }));
          },
        },
        {
          type: "select" as const,
          label: "Password Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.passwordFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "Where the password should be placed in the request",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              passwordFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "MSISDN Field Name",
          placeholder: "e.g., msisdn",
          value: dialogData.msisdnFieldName || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          tooltip: "The name of the MSISDN field in the service provider's API",
          error: errors.msisdnFieldName || false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, msisdnFieldName: String(value) }));
            setErrors((prev) => ({ ...prev, msisdnFieldName: !value }));
          },
        },
        {
          type: "select" as const,
          label: "MSISDN Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.msisdnFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          tooltip: "Where the MSISDN should be placed in the request",
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              msisdnFieldLocation: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "HTTP Method",
          placeholder: "Select Method",
          options: httpMethodOptions,
          value: dialogData.httpMethod || "GET",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          error: errors.httpMethod || false,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              httpMethod: String(value),
            }));
            setErrors((prev) => ({ ...prev, httpMethod: !value }));
          },
        },
        {
          type: "text" as const,
          label: "Content Type",
          placeholder: "application/json",
          value: dialogData.contentType || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, contentType: String(value) }));
          },
        },
      ],
      // Step 3: Response Field Details
      [
        ...(dialogData.type === "MNP" || dialogData.type === "MNP_HLR" ? [
          {
            type: "text" as const,
            label: "MCC Field",
            placeholder: "e.g., mcc",
            value: dialogData.responseMcc || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "The name of the MCC (Mobile Country Code) field in the response",
            error: errors.responseMcc || false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, responseMcc: String(value) }));
              setErrors((prev) => ({ ...prev, responseMcc: !value }));
            },
          },
          {
            type: "text" as const,
            label: "MNC Field",
            placeholder: "e.g., mnc",
            value: dialogData.responseMnc || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "The name of the MNC (Mobile Network Code) field in the response",
            error: errors.responseMnc || false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, responseMnc: String(value) }));
              setErrors((prev) => ({ ...prev, responseMnc: !value }));
            },
          },
          {
            type: "text" as const,
            label: "Original MNC Field",
            placeholder: "e.g., original_mnc",
            value: dialogData.responseOriginalMnc || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "The name of the original MNC field in the response",
            error: errors.responseOriginalMnc || false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, responseOriginalMnc: String(value) }));
              setErrors((prev) => ({ ...prev, responseOriginalMnc: !value }));
            },
          },
          {
            type: "text" as const,
            label: "Ported Field",
            placeholder: "e.g., ported or is_ported",
            value: dialogData.responsePorted || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "The name of the ported status field in the response",
            error: errors.responsePorted || false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, responsePorted: String(value) }));
              setErrors((prev) => ({ ...prev, responsePorted: !value }));
            },
          },
        ] : []),
        // Type-specific fields
        ...(dialogData.type === "HLR" || dialogData.type === "MNP_HLR" ? [
          {
            type: "text" as const,
            label: "Active Field",
            placeholder: "e.g., active or is_active",
            value: dialogData.responseNotActive || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "The name of the active status field in the response",
            error: errors.responseNotActive || false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, responseNotActive: String(value) }));
              setErrors((prev) => ({ ...prev, responseNotActive: !value }));
            },
          },
          {
            type: "text" as const,
            label: "Not Active Values",
            placeholder: "e.g., 0,false,inactive",
            value: dialogData.responseNotActiveValues || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            tooltip: "Comma-separated values that indicate inactive status",
            error: errors.responseNotActiveValues || false,
            onChange: (value: string | number) => {
              const strValue = String(value);
              // Validate comma-separated format (allow empty or values separated by commas)
              const isValid = strValue === "" || /^[^,]+(,[^,]+)*$/.test(strValue.trim());
              setDialogData((prev) => ({ ...prev, responseNotActiveValues: strValue }));
              setErrors((prev) => ({ ...prev, responseNotActiveValues: !isValid }));
            },
          },
          
        ] : []),
        {
          type: "text" as const,
          label: "Response Code Field",
          placeholder: "e.g., response_code or status",
          value: dialogData.responseCode || "",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          tooltip: "The name of the response code field in the response (Will be saved in the cdr)",
          error: errors.responseCode || false,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, responseCode: String(value) }));
            setErrors((prev) => ({ ...prev, responseCode: !value }));
          },
        },
        {
          type: "text" as const,
          hidden: true,
          value: "",
          onChange: () => {},
        },
      ],
    ];

    return steps[currentStep] || [];
  };

  // Define a type for the flattened history row
  type FlattenedHistoryRow = IServiceProvider & IServiceProviderHistory & {
    requestFieldApiKeyName: string;
    requestFieldApiKeyLocation: string;
    requestFieldMsisdnName: string;
    requestFieldMsisdnLocation: string;
    requestFieldHttpMethod: string;
    requestFieldContentType: string;
    responseFieldMcc: string;
    responseFieldMnc: string;
    responseFieldOriginalMnc: string;
    responseFieldPorted: string;
    responseFieldNotActive: string;
    responseFieldNotActiveValues: string;
    responseFieldResponseCode: string;
  };

  const fieldHistory: IField<FlattenedHistoryRow>[] = [
    { key: "name", header: "Name" },
    {
      key: "profileId",
      header: "Profile",
      transform: (row) => {
        if (!row.profileId) return "Unknown";
        const profile = profileMap[row.profileId];
        return profile?.name || "Unknown";
      },
    },
    { key: "type", header: "Type" },
    { key: "protocol", header: "Protocol" },
    { key: "baseUrl", header: "Base URL" },
    { key: "apiKey", header: "API Key" },
    {
      key: "enabled",
      header: "Status",
      transform: (row) => (row.enabled ? "Enabled" : "Disabled"),
    },
    // Request Field Details - using flattened keys for individual diff highlighting
    {
      key: "requestFieldApiKeyName",
      header: "API Key Field Name",
      transform: (row) => row.requestFieldApiKeyName || "-",
    },
    {
      key: "requestFieldApiKeyLocation",
      header: "API Key Location",
      transform: (row) => row.requestFieldApiKeyLocation || "-",
    },
    {
      key: "requestFieldMsisdnName",
      header: "MSISDN Field Name",
      transform: (row) => row.requestFieldMsisdnName || "-",
    },
    {
      key: "requestFieldMsisdnLocation",
      header: "MSISDN Location",
      transform: (row) => row.requestFieldMsisdnLocation || "-",
    },
    {
      key: "requestFieldHttpMethod",
      header: "HTTP Method",
      transform: (row) => row.requestFieldHttpMethod || "-",
    },
    {
      key: "requestFieldContentType",
      header: "Content Type",
      transform: (row) => row.requestFieldContentType || "-",
    },
    // Response Field Details - using flattened keys for individual diff highlighting
    {
      key: "responseFieldMcc",
      header: "Response MCC Field",
      transform: (row) => row.responseFieldMcc || "-",
    },
    {
      key: "responseFieldMnc",
      header: "Response MNC Field",
      transform: (row) => row.responseFieldMnc || "-",
    },
    {
      key: "responseFieldOriginalMnc",
      header: "Response Original MNC Field",
      transform: (row) => row.responseFieldOriginalMnc || "-",
    },
    {
      key: "responseFieldPorted",
      header: "Response Ported Field",
      transform: (row) => row.responseFieldPorted || "-",
    },
    {
      key: "responseFieldNotActive",
      header: "Response Not Active Field",
      transform: (row) => row.responseFieldNotActive || "-",
    },
    {
      key: "responseFieldNotActiveValues",
      header: "Response Not Active Values",
      transform: (row) => row.responseFieldNotActiveValues || "-",
    },
    {
      key: "responseFieldResponseCode",
      header: "Response Code Field",
      transform: (row) => row.responseFieldResponseCode || "-",
    },
    { key: "historyTimestamp" as keyof FlattenedHistoryRow, header: "Date Modified" },
    { key: "createdBy", header: "Created By" },
    { key: "updatedBy", header: "Updated By" },
  ];

  const dialogButtons: ICustomButtonProps[] =
    dialogMode === "history"
      ? []
      : dialogMode === "delete"
      ? [
          {
            label: "Delete",
            onClick: handleDelete,
            variant: "contained",
            bgColor: COLORS.GOLD,
            permission: "DELETE_SERVICE_PROVIDER",
          },
        ]
      : [
          ...(currentStep > 0
            ? [
                {
                  label: "Back",
                  onClick: () => setCurrentStep((prev) => prev - 1),
                  bgColor: COLORS.GOLD,
                },
              ]
            : []),
          ...(currentStep < stepLabels.length - 1
            ? [
                {
                  label: "Next",
                  onClick: () => setCurrentStep((prev) => prev + 1),
                  bgColor: COLORS.GOLD,
                  disabled: isNextDisabled,
                },
              ]
            : [
                {
                  label: "Finish",
                  onClick: handleSave,
                  bgColor: COLORS.GOLD,
                  disabled: isSaveDisabled || (dialogMode === "edit" ? !hasJwtPermission("PUT_SERVICE_PROVIDER") : !hasJwtPermission("POST_SERVICE_PROVIDER")),
                },
              ]),
        ];

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        setCurrentStep(0);
        setDialogData(initialDialogData);
      }}
      mode={dialogMode}
      dialogData={{ accountType: "serviceProvider" }}
      {...(dialogMode !== "history" && dialogMode !== "delete"
        ? {
            onNext: () =>
              setCurrentStep((prev) =>
                prev < stepLabels.length - 1 ? prev + 1 : prev
              ),
            onBack: () => setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev)),
            stepLabels: stepLabels,
            activeStep: currentStep,
          }
        : {})}
      title={
        dialogMode === "history"
          ? "History"
          : dialogMode === "delete"
          ? "Delete Service Provider"
          : dialogMode === "edit"
          ? "Edit Service Provider"
          : "Add Service Provider"
      }
      fields={getStepFields()}
      fieldsPerRow={dialogMode === "delete" ? undefined : 2}
      buttons={dialogButtons}
    />
  );
});

// Test Service Provider Dialog Component
const TestServiceProviderDialog = forwardRef<TestServiceProviderDialogRef, {}>(
  (_, ref) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [dialogOpen, setDialogOpen] = useState(false);
    const [serviceProviderId, setServiceProviderId] = useState<number | null>(null);
    const [serviceProviderName, setServiceProviderName] = useState<string>("");
    const [msisdn, setMsisdn] = useState<string>("");
    const [isExecuting, setIsExecuting] = useState(false);
    const [testResponse, setTestResponse] = useState<ITestingServiceProvider | null>(null);
    const [testError, setTestError] = useState<string | null>(null);
    const [filters, setfilters] = useState<any>({});
    const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);

    useImperativeHandle(ref, () => ({
      openDialog: async (id: string) => {
        try {
          const provider = await getServiceProviderById(id);
          setServiceProviderId(provider.id);
          setServiceProviderName(provider.name);
          setMsisdn("");
          setTestResponse(null);
          setTestError(null);
          setDialogOpen(true);
          // Set initial filters without refresh to prevent immediate API call
          setfilters({ serviceProviderId: provider.id });
        } catch (error: any) {
          console.error("Failed to load service provider details:", error);
          ToastNotification.error("Failed to load service provider details.");
        }
      },
    }));

    // Cleanup polling on unmount or dialog close
    useEffect(() => {
      return () => {
        if (pollingIntervalId) {
          clearInterval(pollingIntervalId);
        }
      };
    }, [pollingIntervalId]);

    const pollTestResult = useCallback(async (testId: number, intervalId: NodeJS.Timeout) => {
      try {
        const result = await getTestingServiceProviderById(testId);
        
        if (result.status === "DONE" || result.status === "ERROR") {
          // Stop polling
          clearInterval(intervalId);
          setPollingIntervalId(null);
          setTestResponse(result);
          setIsExecuting(false);
          
          // Refresh history
          if (serviceProviderId) {
            setfilters({ serviceProviderId, _refresh: Date.now() });
          }
        } else {
          // Still pending, update the response
          setTestResponse(result);
        }
      } catch (error: any) {
        console.error("Error polling test result:", error);
        clearInterval(intervalId);
        setPollingIntervalId(null);
        setIsExecuting(false);
        setTestError(error.message || "Failed to fetch test result.");
      }
    }, [serviceProviderId]);

    const handleExecuteTest = async () => {
      if (!serviceProviderId || !msisdn.trim()) {
        setTestError("Please enter a phone number (MSISDN).");
        return;
      }

      setIsExecuting(true);
      setTestResponse(null);
      setTestError(null);

      try {
        const response = await executeServiceProviderTest({
          serviceProviderId,
          msisdn: msisdn.trim(),
        });
        
        // Set initial pending response
        setTestResponse(response as any);
        
        // Only start polling if the status is PENDING
        if (response.status === "PENDING") {
          // Start polling for the result
          const intervalId = setInterval(() => {
            pollTestResult(response.id, intervalId);
          }, 2000); // Poll every 2 seconds
          
          setPollingIntervalId(intervalId);
          
          // Also do an immediate poll
          pollTestResult(response.id, intervalId);
        } else {
          // If not pending, just set as final result
          setIsExecuting(false);
          if (serviceProviderId) {
            setfilters({ serviceProviderId, _refresh: Date.now() });
          }
        }
        
      } catch (error: any) {
        setTestError(error.message || "An error occurred while executing the test.");
        setIsExecuting(false);
      }
    };

    const handleClose = () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
        setPollingIntervalId(null);
      }
      setDialogOpen(false);
      setServiceProviderId(null);
      setServiceProviderName("");
      setMsisdn("");
      setTestResponse(null);
      setTestError(null);
      setfilters({});
      setIsExecuting(false);
    };

    const formatJsonResponse = useCallback((response: string | null | undefined): string => {
      if (!response) return "N/A";
      try {
        const parsed = JSON.parse(response);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return response;
      }
    }, []);

    const getStatusColor = useCallback((status: string): "success" | "error" | "warning" | "default" => {
      switch (status?.toUpperCase()) {
        case "DONE":
          return "success";
        case "ERROR":
          return "error";
        case "PENDING":
          return "warning";
        default:
          return "default";
      }
    }, []);

    const MetadataItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
      <Box>
        <Typography
          variant="body2"
          sx={{ color: COLORS.typographyColor, opacity: 0.7, mb: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.typographyColor }}>
          {value}
        </Typography>
      </Box>
    );

    // Memoized component for displaying country name with caching
    const CountryCell = React.memo(({ countryId }: { countryId?: number }) => {
      const { data: countryName } = useQuery({
        queryKey: ["country", countryId],
        queryFn: async () => {
          if (!countryId) return "N/A";
          try {
            const country = await fetchCountry(countryId);
            return country.name;
          } catch {
            return "N/A";
          }
        },
        enabled: !!countryId,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      });
      
      return <>{countryName || (countryId ? "Loading..." : "N/A")}</>;
    });
    CountryCell.displayName = "CountryCell";

    // Memoized component for displaying operator name with caching
    const OperatorCell = React.memo(({ operatorId }: { operatorId?: number }) => {
      const { data: operatorName } = useQuery({
        queryKey: ["operator", operatorId],
        queryFn: async () => {
          if (!operatorId) return "N/A";
          try {
            const operator = await fetchOperator(operatorId);
            return operator.name;
          } catch {
            return "N/A";
          }
        },
        enabled: !!operatorId,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      });
      
      return <>{operatorName || (operatorId ? "Loading..." : "N/A")}</>;
    });
    OperatorCell.displayName = "OperatorCell";

    // Component for displaying response with copy functionality
    const ResponseCell = React.memo(({ response }: { response?: string }) => {
      const [showCopied, setShowCopied] = useState(false);
      
      const formattedResponse = React.useMemo(() => {
        if (!response) return '';
        try {
          const parsed = JSON.parse(response);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return response;
        }
      }, [response]);
      
      if (!response) return <>N/A</>;
      
      const truncated = response.length > 50 
        ? response.substring(0, 50) + "..." 
        : response;
      
      const handleCopyResponse = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(response);
          } else {
            const textArea = document.createElement("textarea");
            textArea.value = response;
            textArea.style.position = "fixed";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
          }
          setShowCopied(true);
          setTimeout(() => setShowCopied(false), 1500);
        } catch (error) {
          console.error("Failed to copy response:", error);
        }
      };
      
      const tooltipContent = showCopied ? (
        "Copied!"
      ) : (
        <Box>
          <pre style={{ margin: 0, marginBottom: 8, whiteSpace: "pre-wrap" }}>{formattedResponse}</pre>
          <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.3)" }} />
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Click to copy
          </Typography>
        </Box>
      );
      
      return (
        <Tooltip title={tooltipContent} arrow>
          <Box
            component="span"
            onClick={handleCopyResponse}
            sx={{
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: "0.85em",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {truncated}
          </Box>
        </Tooltip>
      );
    });
    ResponseCell.displayName = "ResponseCell";

    const historyFields: IField<ITestingServiceProvider>[] = React.useMemo(() => [
      { key: "destinationNumber", header: "Phone Number" },
      { key: "type", header: "Type" },
      {
        key: "cost",
        header: "Cost",
        transform: (row) => (row.cost !== undefined && row.cost !== null ? row.cost.toString() : "N/A"),
      },
      {
        key: "status",
        header: "Status",
        transform: (row) => (
          <Chip
            label={row.status || "N/A"}
            color={getStatusColor(row.status)}
            size="small"
            clickable={false}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        key: "countryId",
        header: "Country",
        transform: (row) => <CountryCell countryId={row.countryId} />,
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: (row) => <OperatorCell operatorId={row.operatorId} />,
      },
      {
        key: "response",
        header: "Response",
        transform: (row) => <ResponseCell response={row.response} />,
      },
    ], [CountryCell, OperatorCell, ResponseCell, getStatusColor]);

    const dialogFields = [
      {
        type: "custom" as const,
        component: (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            {/* Test Execution Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: COLORS.typographyColor }}>
                Execute API Test
              </Typography>
              
              <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: 2, alignItems: "flex-end" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomTextField
                    label="Phone Number (MSISDN)"
                    value={msisdn}
                    onChange={(e) => {
                      setMsisdn(e.target.value);
                      // Clear any previous error when user starts typing
                      if (testError) {
                        setTestError(null);
                      }
                    }}
                    placeholder="Enter phone number"
                    bgColor={COLORS.WhiteBackground}
                    highlightLabelStar={true}
                    fullWidth
                    disabled={isExecuting}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isExecuting && msisdn.trim()) {
                        handleExecuteTest();
                      }
                    }}
                  />
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  <button
                    onClick={handleExecuteTest}
                    disabled={isExecuting || !msisdn.trim() || !hasJwtPermission("EXECUTE_TESTING_SERVICE_PROVIDER")}
                    style={{
                      padding: "12px 24px",
                      backgroundColor: isExecuting || !msisdn.trim() || !hasJwtPermission("EXECUTE_TESTING_SERVICE_PROVIDER") ? "#ccc" : COLORS.GOLD,
                      color: COLORS.WhiteBackground,
                      border: "none",
                      borderRadius: "12px",
                      cursor: isExecuting || !msisdn.trim() || !hasJwtPermission("EXECUTE_TESTING_SERVICE_PROVIDER") ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: "140px",
                      justifyContent: "center",
                    }}
                  >
                    {isExecuting && <CircularProgress size={16} sx={{ color: COLORS.WhiteBackground }} />}
                    {isExecuting ? "Executing..." : "Execute Test"}
                  </button>
                </Box>
              </Box>
            </Box>

            {/* Test Response Section */}
            {testError && (
              <Alert severity="error">{testError}</Alert>
            )}

            {testResponse && (
              <Alert severity={testResponse.status === "DONE" ? "success" : testResponse.status === "ERROR" ? "error" : "info"}>
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {testResponse.status === "PENDING" ? "Test in progress..." : testResponse.status === "DONE" ? "Test completed successfully" : "Test failed"}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: isSmallScreen ? "1fr" : "1fr 1fr",
                      gap: 1.5,
                      mb: testResponse.response ? 2 : 0,
                    }}
                  >
                    <MetadataItem
                      label="Status"
                      value={
                        <Chip
                          label={testResponse.status}
                          color={getStatusColor(testResponse.status)}
                          size="small"
                          clickable={false}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                    />
                    <MetadataItem label="Type" value={testResponse.type} />
                    {testResponse.cost !== undefined && testResponse.cost !== null && (
                      <MetadataItem label="Cost" value={testResponse.cost} />
                    )}
                    <MetadataItem
                      label="Phone Number"
                      value={testResponse.destinationNumber}
                    />
                  </Box>
                  {testResponse.response && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Response Data
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          p: 2,
                          backgroundColor: "#1e1e1e",
                          color: "#d4d4d4",
                          borderRadius: 1,
                          overflow: "auto",
                          maxHeight: "200px",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          margin: 0,
                        }}
                      >
                        {formatJsonResponse(testResponse.response)}
                      </Box>
                    </>
                  )}
                  {isExecuting && testResponse.status === "PENDING" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Waiting for response...</Typography>
                    </Box>
                  )}
                </Box>
              </Alert>
            )}

            <Divider />

            {/* Testing Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: COLORS.typographyColor }}>
                Testing Sessions
              </Typography>
              
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <CustomTable<ITestingServiceProvider>
                  filters={{ 
                    ...filters,
                    sort: filters.sort || "createdDate,desc"
                  }}
                  fields={historyFields}
                  fetchFunc={useTestingServiceProvidersQuery}
                  sortableFields={["destinationNumber", "createdDate", "cost", "status"]}
                />
              </Box>
            </Box>
          </Box>
        ),
      },
    ];

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={handleClose}
        mode="confirm"
        type="confirm"
        title={`Test API - ${serviceProviderName}`}
        fields={dialogFields}
        buttons={[]}
        contentSx={{ overflowX: "hidden" }}
        dialogSx={{
          minWidth: isSmallScreen ? "100vw" : "900px",
          maxWidth: isSmallScreen ? "100vw" : "1000px",
        }}
      />
    );
  }
);

export default ServiceProviderPage;
